/**
 * "Plan from your code" card on the instance page (OCP-911, OCP-917).
 *
 * A workspace provisioned from an invite / prepaid code runs on the code's
 * plan until its grant end and keeps the full plan through a grace period.
 * When the grace ends without a subscription the workspace is shut down
 * (read-only, closed page); its data stays exportable under Backups until
 * the end of the AGB 12.3 window, and a subscription brings it back. A
 * managed workspace never runs as Community. This card says where the grant
 * stands and lets the account switch THIS workspace to a subscription: the
 * ordinary order review (summary, Terms, responsible contact) with the plan
 * and the seat count from the code, then `checkout/create` with `tenant_id`.
 * When the subscription goes live the payment webhook records it on this
 * workspace; no new workspace is created.
 *
 * The reminder mails link here with `?convert=1`, which scrolls to the card
 * and highlights the conversion. Renders nothing for a workspace that did not
 * come from a code (the endpoint answers 404).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { contractGateMessage } from './BusinessCapacityGate';
import { useOrderReview } from './OrderReviewStep';
import { ApiError, api } from '../lib/api';
import { fmtDate } from '../lib/format-time';
import { usePortalAccess } from '../lib/use-portal-access';

export interface LicenseGrant {
  tenant_id: string;
  state: 'running' | 'expiring' | 'in_grace' | 'shut_down' | 'covered' | 'lifetime' | 'scratch';
  tier: 'team' | 'business';
  seats: number | null;
  grant_ends_at: string | null;
  grace_ends_at: string | null;
  /** OCP-917 - when the workspace was shut down (null while it runs). */
  shut_down_at: string | null;
  /** OCP-917 - the day it shuts down without a subscription (for a shut-down one: the day it was). */
  shuts_down_at: string | null;
  /** OCP-917 - the end of the AGB 12.3 export window after that shutdown. */
  export_until: string | null;
  paused: boolean;
  days_until_end: number | null;
  covered: boolean;
  converted_at: string | null;
  convertible: boolean;
  not_convertible_reason: 'covered' | 'lifetime' | 'not_live' | 'scratch' | null;
  convert_seats: number;
  products: Array<{ id: string; display_name: string; billing_period: string; price_eur_cents: number; seat_based: boolean }>;
}

const PLAN: Record<string, string> = { team: 'Team', business: 'Business' };

export function LicenseGrantCard({ instanceId }: { instanceId: string }) {
  const { t } = useTranslation('instance-detail');
  const { canConcludeContract } = usePortalAccess();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlight = searchParams.get('convert') === '1';
  const checkoutReturned = searchParams.get('checkout') === 'success';
  const [grant, setGrant] = useState<LicenseGrant | null>(null);
  const [absent, setAbsent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null);
  const ref = useRef<HTMLElement | null>(null);
  const orderReview = useOrderReview();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const g = await api<LicenseGrant>(`/v1/portal/instances/${instanceId}/license-grant`);
        if (!cancelled) setGrant(g);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setAbsent(true);
        else setError(t('grant.loadFailed'));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [instanceId, t]);

  useEffect(() => {
    if (highlight && grant && ref.current && typeof ref.current.scrollIntoView === 'function') {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [highlight, grant]);

  const runCheckout = useCallback(
    async (productId: string, seatCount: number | null, discountCode: string | null) => {
      setPending(productId);
      setError(null);
      try {
        const r = await api<{ checkout_url: string }>('/v1/portal/checkout/create', {
          method: 'POST',
          body: {
            product_id: productId,
            tenant_id: instanceId,
            success_url: `${window.location.origin}/instances/${instanceId}?checkout=success`,
            ...(seatCount !== null ? { seat_count: seatCount } : {}),
            // OCP-991 - the conversion checkout carries the code from the waitlist.
            ...(discountCode ? { discount_code: discountCode } : {}),
          },
        });
        window.location.assign(r.checkout_url);
      } catch (err) {
        setPending(null);
        if (err instanceof ApiError && err.status === 401) {
          navigate('/login?from=' + encodeURIComponent(`/instances/${instanceId}?convert=1`));
          return;
        }
        setError(contractGateMessage(err) ?? t('grant.convert.failed'));
      }
    },
    [instanceId, navigate, t],
  );

  if (absent) return null;
  if (error && !grant) {
    return (
      <p role="alert" className="mt-4 text-sm text-red-700 dark:text-red-300">
        {error}
      </p>
    );
  }
  if (!grant) return null;

  const plan = PLAN[grant.tier] ?? grant.tier;
  const date = fmtDate(grant.grant_ends_at, '-');
  const stateText = t(`grant.state.${grant.state}`, {
    plan,
    date,
    shutDate: fmtDate(grant.shuts_down_at, '-'),
    exportDate: fmtDate(grant.export_until, '-'),
    days: grant.days_until_end ?? 0,
  });
  const showExport = (grant.state === 'in_grace' || grant.state === 'shut_down') && grant.export_until !== null;
  const tone =
    grant.state === 'shut_down'
      ? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30'
      : grant.state === 'in_grace'
        ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30'
        : grant.state === 'expiring' || highlight
          ? 'border-brand-200 dark:border-brand-800 bg-brand-50 dark:bg-brand-900/20'
          : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900';

  return (
    <section
      ref={ref}
      id="convert"
      data-testid="license-grant-card"
      className={`mt-4 rounded-lg border p-5 ${tone}`}
    >
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{t('grant.title')}</h2>
      <p data-testid="license-grant-state" className="mt-1 text-sm text-gray-800 dark:text-gray-200">
        {stateText}
      </p>
      {showExport && (
        <p className="mt-1 text-sm">
          <Link to="/backups" data-testid="license-grant-backups" className="font-medium text-brand-600 hover:underline dark:text-brand-400">
            {t('grant.backupsLink')}
          </Link>
        </p>
      )}
      {grant.converted_at && (
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{t('grant.convertedOn', { date: fmtDate(grant.converted_at, '-') })}</p>
      )}
      {checkoutReturned && !grant.covered && (
        <p role="status" data-testid="license-grant-checkout-returned" className="mt-2 text-sm text-gray-800 dark:text-gray-200">
          {t('grant.convert.returned')}
        </p>
      )}

      {grant.convertible && (
        <div className="mt-3">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {t('grant.convert.intro', { plan, seats: grant.convert_seats })}
          </p>
          {!canConcludeContract ? (
            <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{t('grant.convert.roleOnly')}</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {grant.products.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid="license-grant-convert"
                  disabled={pending !== null}
                  onClick={() =>
                    orderReview.start({
                      productId: p.id,
                      seatCount: grant.convert_seats,
                      onOrder: ({ seatCount, discountCode }) => runCheckout(p.id, seatCount, discountCode),
                    })
                  }
                  className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {pending === p.id
                    ? t('grant.convert.starting')
                    : t('grant.convert.cta', { period: t(`grant.period.${p.billing_period === 'yearly' ? 'yearly' : 'monthly'}`), name: p.display_name })}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {!grant.convertible && grant.not_convertible_reason === null && grant.products.length === 0 && (
        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{t('grant.convert.noProduct')}</p>
      )}
      {error && grant && (
        <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </p>
      )}
      {orderReview.element}
    </section>
  );
}
